import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { ButtonStyle } from '@cineamo/ui/src/button/ButtonStyle.enum';
import Button from '@cineamo/ui/src/button/NewButton';
import { IconDown16 } from '@cineamo/ui/src/icons/ic-down';
import { IconForth16 } from '@cineamo/ui/src/icons/ic-forth';
import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import { IconUp16 } from '@cineamo/ui/src/icons/ic-up';
import { Seat, SeatCategory } from '@cineamo/ui/src/seat-map/typedefs';

import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

import type { BuyerDetails } from './actions';
import CartSummary from './CartSummary';
import { payButtonLabel } from './CheckoutForm';
import ExpressCheckout, { type Wallet } from './ExpressCheckout';
import { MINIMUM_AMOUNT_DUE } from './GiftCardRedemption';
import SeatMapLegend, { type SeatMapLegendFurniture } from './SeatMapLegend';

const CheckoutButton: React.FC<{
    onCheckoutClick: () => void;
    /** The room's seats, for the list of the selected ones in the footer's details. */
    seats: Seat[];
    seatCategories: SeatCategory[];
    hasTables: boolean;
    hasWheelchairSeats: boolean;
    furniture?: SeatMapLegendFurniture;
    isLoading: boolean;
    /** Passed to the seats in the footer's details. */
    onRemoveSeat?: (seatId: Seat['id']) => void;
    /**
     * The payment step (CIN-15367): the pay button names the chosen method, and for Apple Pay or Google Pay
     * (`'wallet'`) it is the wallet's own button. `onWalletChange` reports which wallet the device offers.
     */
    express?: {
        requestPaymentIntent: (buyer: BuyerDetails) => Promise<string | 'paid' | null>;
        onPaymentComplete: () => void;
        selectedMethod: string | null;
        onWalletChange: (wallet: Wallet | null) => void;
    };
}> = ({
    onCheckoutClick,
    seats,
    seatCategories,
    hasTables,
    hasWheelchairSeats,
    furniture,
    isLoading,
    onRemoveSeat,
    express
}) => {
    const t = useTranslations('tickets');
    const formatter = useFormat();
    const {
        selectedSeats,
        total: totalPrice,
        giftCardDeduction,
        amountDue,
        currency,
        currentStep,
        hasNoPriceOptions
    } = useCartProvider();
    const hideButton = !['01_seat_picking', '01.5_cart', '02_payment'].includes(currentStep);
    // The seat picking step prices the cart client-side, ahead of the sync that lets the API price the gift card.
    const hasGiftCard = giftCardDeduction > 0 && currentStep !== '01_seat_picking';
    const totalAmount = formatter.number.formatCurrency(totalPrice / 100, currency);
    const amount = hasGiftCard ? formatter.number.formatCurrency(amountDue / 100, currency) : totalAmount;
    const isFullyCovered = hasGiftCard && amountDue === 0;
    const buttonText = {
        '01_seat_picking': t('continue'),
        '01.5_cart': isFullyCovered ? t('continue') : t('continue-with-payment'),
        '02_payment': isFullyCovered ? t('complete-booking') : payButtonLabel(t, express?.selectedMethod, amount)
    }[currentStep as '01_seat_picking' | '01.5_cart' | '02_payment'];

    const isCheckoutStep = currentStep === '01.5_cart' || currentStep === '02_payment';
    const isAmountDueTooLow = hasGiftCard && amountDue > 0 && amountDue < MINIMUM_AMOUNT_DUE;
    // Not tied to `isLoading`: unmounting the element would end a wallet payment in progress.
    const isExpressShown =
        !!express && currentStep === '02_payment' && selectedSeats.length > 0 && amountDue >= MINIMUM_AMOUNT_DUE;
    const [hasWallet, setHasWallet] = useState(false);
    const isWalletPrimary = isExpressShown && hasWallet && express.selectedMethod === 'wallet';
    const isDisabled =
        isLoading ||
        hasNoPriceOptions ||
        selectedSeats.length <= 0 ||
        (currentStep === '01.5_cart' && isAmountDueTooLow);

    // Small screens, seat picking and payment: the selected seats (with the price category hint while picking), which
    // would sit below the map or crowd the payment methods, fold out of the footer. Its toggle shows once a seat is picked.
    const hasDetails = ['01_seat_picking', '02_payment'].includes(currentStep) && selectedSeats.length > 0;
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    // Closed with the last seat, so picking a seat again does not lay them over the map unasked.
    if (isDetailsOpen && selectedSeats.length === 0) setIsDetailsOpen(false);
    const isDetailsShown = hasDetails && isDetailsOpen;

    // The sidebar keeps this much space free at the page's end on small screens, where the footer is fixed over it.
    const footerRef = useRef<HTMLDivElement>(null);
    const detailsRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const footer = footerRef.current;
        if (!footer) return;
        const root = document.documentElement;
        // Without the open details: opened, they lie over the map instead of shrinking it.
        const observer = new ResizeObserver(() =>
            root.style.setProperty(
                '--booking-footer-height',
                `${footer.offsetHeight - (detailsRef.current?.offsetHeight ?? 0)}px`
            )
        );
        observer.observe(footer);
        return () => {
            observer.disconnect();
            root.style.removeProperty('--booking-footer-height');
        };
    }, []);

    const containerClassName = cn(
        'left-0 right-0',
        'fixed lg:sticky',
        'z-[900] w-full',
        'bottom-0',
        'overflow-clip rounded-t-16 lg:rounded-t-8',
        // Small screens, per Figma (Cinema Websites 503:89279): the foreground at 10 % over the page, opaque, shadowed.
        '[background:linear-gradient(rgba(var(--on-background-color-rgb),0.1),rgba(var(--on-background-color-rgb),0.1)),var(--background-color)]',
        'shadow-[0_-8px_16px_0_rgba(0,0,0,0.2)] lg:shadow-none text-[var(--on-background-color)]',
        'lg:[background:var(--non-transparent-surface-color)] lg:text-[var(--on-non-transparent-surface-color)]'
    );

    if (hasNoPriceOptions) {
        return (
            <div className={containerClassName}>
                <p
                    role="alert"
                    className="p-16 text-body-bold-14">
                    {t('booking-unavailable')}
                </p>
            </div>
        );
    }

    return (
        <div
            ref={footerRef}
            data-booking-footer
            className={containerClassName}>
            {/* The toggle tops the sheet, open or closed, and the details unfold below it. */}
            {hasDetails && (
                <button
                    type="button"
                    onClick={() => setIsDetailsOpen((value) => !value)}
                    aria-expanded={isDetailsShown}
                    aria-controls={isDetailsShown ? 'booking-footer-details' : undefined}
                    className="flex w-full items-center justify-center gap-4 px-[9px] py-[10px] text-body-bold-12 outline-none focus-visible:underline lg:hidden">
                    {t(isDetailsShown ? 'hide-details' : 'show-details')}
                    {isDetailsShown ? <IconDown16 fill="currentColor" /> : <IconUp16 fill="currentColor" />}
                </button>
            )}
            {isDetailsShown && (
                <div
                    ref={detailsRef}
                    id="booking-footer-details"
                    // 12px to the legend or the total below, like the gap under the hint above.
                    className="pb-12 lg:hidden">
                    <CartSummary
                        seats={seats}
                        onRemoveSeat={onRemoveSeat}
                        className="max-h-[40svh] overflow-y-auto"
                    />
                </div>
            )}
            <div>
                {/* Mobile, as in the booking design: the legend, then seats, total and the continue button in one row. */}
                <div className="flex flex-col lg:hidden">
                    {currentStep === '01_seat_picking' && (
                        <SeatMapLegend
                            renderSeatTypes={{
                                unavailable: true,
                                selected: true,
                                wheelchair: hasWheelchairSeats,
                                table: hasTables,
                                ...furniture
                            }}
                            seatCategories={seatCategories}
                            className={cn('px-[9px] pb-4 pt-[10px]', hasDetails && 'pt-0')}
                        />
                    )}
                    {isCheckoutStep ? (
                        // Cart and payment, per Figma (Cinema Websites 503:89279): the total over a full-width button.
                        <div className="flex flex-col gap-8 px-[9px] py-16">
                            <div className="flex items-center justify-between gap-16">
                                <div className="flex min-w-0 flex-col">
                                    <span className="text-body-bold-16 leading-[28px] tracking-[0.8px]">
                                        {hasGiftCard ? t('gift-card.amount-due') : t('total')}
                                    </span>
                                    <span className="text-body-14 font-montserrat-medium leading-[24px] tracking-[0.28px] opacity-50">
                                        {t('seats-count', { count: selectedSeats.length })} · {t('incl-fees-and-taxes')}
                                    </span>
                                </div>
                                <span className="shrink-0 text-body-bold-16 leading-[28px] tracking-[0.8px]">
                                    {amount}
                                </span>
                            </div>
                            {!hideButton && !isWalletPrimary && (
                                <button
                                    type="button"
                                    onClick={onCheckoutClick}
                                    disabled={isDisabled}
                                    className={cn(
                                        'flex h-[46px] w-full items-center justify-center gap-8 rounded-8 px-16',
                                        'text-body-bold-16 leading-[26px] tracking-[0.8px]',
                                        'bg-[var(--primary-color)] text-[var(--on-primary-color)]',
                                        'outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)]',
                                        'disabled:cursor-not-allowed disabled:opacity-50'
                                    )}>
                                    {isLoading && <IconLoading16 className="animate-spin" />}
                                    {buttonText}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-16 px-[9px] py-16">
                            <div className="flex flex-col">
                                <span className="text-body-12 font-montserrat-medium">{t('selected-seats-short')}</span>
                                <span className="text-headline-20">{selectedSeats.length}</span>
                            </div>
                            <div className="flex min-w-0 flex-1 flex-col">
                                <span className="text-body-12 font-montserrat-medium">{t('total-incl-tax')}</span>
                                <span className="text-headline-20">{amount}</span>
                            </div>
                            {!hideButton && (
                                <button
                                    type="button"
                                    onClick={onCheckoutClick}
                                    disabled={isDisabled}
                                    className={cn(
                                        'flex shrink-0 items-center justify-center gap-8 rounded-8 px-16 py-10 text-body-bold-14',
                                        'bg-[var(--primary-color)] text-[var(--on-primary-color)]',
                                        'outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)]',
                                        'disabled:cursor-not-allowed disabled:opacity-50'
                                    )}>
                                    {isLoading && <IconLoading16 className="animate-spin" />}
                                    {buttonText}
                                    <IconForth16 fill="currentColor" />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <div className="hidden w-full flex-col space-y-8 p-16 lg:flex">
                    <div className="flex flex-row space-x-16 justify-between items-center">
                        <div className="flex flex-col space-y-4">
                            <span className="text-body-bold-16">{t('total')}</span>
                            <span className="text-body-14 font-montserrat-medium opacity-50">
                                {t('incl-fees-and-taxes')}
                            </span>
                        </div>
                        <span className="text-body-bold-16">{totalAmount}</span>
                    </div>
                    {hasGiftCard && (
                        <>
                            <div className="flex flex-row space-x-16 justify-between items-center text-body-14">
                                <span>{t('gift-card.applied-deduction')}</span>
                                <span>{formatter.number.formatCurrency(-giftCardDeduction / 100, currency)}</span>
                            </div>
                            <div className="flex flex-row space-x-16 justify-between items-center">
                                <span className="text-body-bold-16">{t('gift-card.amount-due')}</span>
                                <span className="text-body-bold-16">{amount}</span>
                            </div>
                        </>
                    )}
                    {!hideButton && !isWalletPrimary && (
                        <Button
                            className="w-full min-h-48"
                            style={ButtonStyle.default}
                            onClick={onCheckoutClick}
                            disabled={isDisabled}
                            leadingIcon={isLoading && <IconLoading16 className="animate-spin" />}>
                            {buttonText}
                        </Button>
                    )}
                </div>
                {isExpressShown && (
                    // Stays mounted while the form is active: unmounting would end a wallet payment in progress.
                    <div className={cn('flex flex-col gap-4 px-[9px] pb-16 lg:px-16', !isWalletPrimary && 'hidden')}>
                        <ExpressCheckout
                            amount={amountDue}
                            currency={currency}
                            requestPaymentIntent={express.requestPaymentIntent}
                            onPaymentComplete={express.onPaymentComplete}
                            onAvailabilityChange={(wallet) => {
                                setHasWallet(!!wallet);
                                express.onWalletChange(wallet);
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default CheckoutButton;
